import { apiAdminClient } from "../_lib/apiClient";

const MAX_SERVICE_DATES = 366;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const QUOTE_FINGERPRINT_PATTERN = /^[a-f0-9]{64}$/;
const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,254}$/;
const OPERATION_STATUS_TIMEOUT_MS = 15000;
const REFUND_COMMIT_TIMEOUT_MS = 30000;
const GUEST_FEE_DISPOSITIONS = Object.freeze({
  REFUND_TO_GUEST: "refund_to_guest",
  RETAIN: "retain",
});

function serviceError(message, code, data) {
  const error = new Error(message);
  error.statusCode = 400;
  error.code = code;
  if (data !== undefined) error.data = data;
  return error;
}

function requiredIdentifier(value, label, code) {
  const identifier = typeof value === "string" ? value.trim() : "";
  if (!identifier) throw serviceError(`${label} is required.`, code);
  return identifier;
}

function isValidDateOnly(value) {
  if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const parsed = new Date(0);
  parsed.setUTCHours(0, 0, 0, 0);
  parsed.setUTCFullYear(year, month - 1, day);
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

function normalizeServiceDates(serviceDates, { required = false } = {}) {
  if (serviceDates === undefined || serviceDates === null) {
    if (required) {
      throw serviceError(
        "Select at least one service date.",
        "service_dates_required",
      );
    }
    return undefined;
  }
  if (!Array.isArray(serviceDates)) {
    throw serviceError(
      "Service dates must be an array.",
      "service_dates_invalid",
    );
  }
  if (serviceDates.length > MAX_SERVICE_DATES) {
    throw serviceError(
      `Select no more than ${MAX_SERVICE_DATES} service dates.`,
      "service_dates_limit_exceeded",
    );
  }
  if (serviceDates.length === 0) {
    if (required) {
      throw serviceError(
        "Select at least one service date.",
        "service_dates_required",
      );
    }
    return undefined;
  }

  const normalized = serviceDates.map((serviceDate) => {
    if (!isValidDateOnly(serviceDate)) {
      throw serviceError(
        "Each service date must be a valid YYYY-MM-DD calendar date.",
        "service_date_invalid",
        { serviceDate },
      );
    }
    return serviceDate;
  });
  if (new Set(normalized).size !== normalized.length) {
    throw serviceError(
      "Service dates must not contain duplicates.",
      "service_dates_duplicate",
    );
  }
  return normalized.sort();
}

function normalizeGuestFeeDisposition(value, { required = false } = {}) {
  if (value === undefined || value === null || value === "") {
    if (required) {
      throw serviceError(
        "Choose whether to return or retain the guest service fee.",
        "guest_fee_disposition_required",
      );
    }
    return undefined;
  }
  if (!Object.values(GUEST_FEE_DISPOSITIONS).includes(value)) {
    throw serviceError(
      "The guest service fee choice is invalid.",
      "guest_fee_disposition_invalid",
    );
  }
  return value;
}

function normalizedResponseData(
  response,
  acceptedStatusCodes,
  fallbackMessage,
) {
  const body = response?.data;
  const statusCode = Number(body?.statusCode ?? response?.status);
  const hasAcceptedStatus =
    Number.isFinite(statusCode) && acceptedStatusCodes.includes(statusCode);
  if (
    !body ||
    body.status !== true ||
    !hasAcceptedStatus ||
    body.data === undefined
  ) {
    const invalidResponse = new Error(
      body?.message || body?.userMessage || fallbackMessage,
    );
    invalidResponse.response = {
      status: Number.isFinite(statusCode) ? statusCode : response?.status,
      data: body,
    };
    throw invalidResponse;
  }
  return body.data;
}

function dateScopedRefundError(error, fallbackMessage) {
  const body = error?.response?.data;
  const structured = new Error(
    body?.message || body?.userMessage || error?.message || fallbackMessage,
  );
  structured.statusCode = body?.statusCode ?? error?.response?.status;
  structured.code = body?.code;
  structured.data = body?.data;
  if (body?.data?.operation) structured.operation = body.data.operation;
  return structured;
}

async function quoteByDates({ bookingId, serviceDates, guestFeeDisposition } = {}) {
  const body = {
    bookingId: requiredIdentifier(
      bookingId,
      "A reservation",
      "booking_id_required",
    ),
  };
  const normalizedDates = normalizeServiceDates(serviceDates);
  if (normalizedDates) {
    body.serviceDates = normalizedDates;
    body.guestFeeDisposition = normalizeGuestFeeDisposition(
      guestFeeDisposition,
      { required: true },
    );
  }

  try {
    const response = await apiAdminClient.post("/refund/quote-by-dates", body);
    return normalizedResponseData(
      response,
      [200],
      "The refund quote returned an invalid response.",
    );
  } catch (error) {
    throw dateScopedRefundError(error, "The refund quote could not be loaded.");
  }
}

async function refundByDates({
  bookingId,
  serviceDates,
  guestFeeDisposition,
  quoteFingerprint,
  idempotencyKey,
} = {}) {
  const normalizedBookingId = requiredIdentifier(
    bookingId,
    "A reservation",
    "booking_id_required",
  );
  const normalizedDates = normalizeServiceDates(serviceDates, {
    required: true,
  });
  const normalizedGuestFeeDisposition = normalizeGuestFeeDisposition(
    guestFeeDisposition,
    { required: true },
  );
  if (
    typeof quoteFingerprint !== "string" ||
    !QUOTE_FINGERPRINT_PATTERN.test(quoteFingerprint)
  ) {
    throw serviceError(
      "A valid refund quote is required.",
      "quote_fingerprint_invalid",
    );
  }
  if (
    typeof idempotencyKey !== "string" ||
    !IDEMPOTENCY_KEY_PATTERN.test(idempotencyKey)
  ) {
    throw serviceError(
      "A valid refund operation key is required.",
      "idempotency_key_invalid",
    );
  }

  try {
    const response = await apiAdminClient.post(
      "/refund/by-dates",
      {
        bookingId: normalizedBookingId,
        serviceDates: normalizedDates,
        guestFeeDisposition: normalizedGuestFeeDisposition,
        quoteFingerprint,
      },
      {
        headers: { "Idempotency-Key": idempotencyKey },
        config: { timeout: REFUND_COMMIT_TIMEOUT_MS },
      },
    );
    return normalizedResponseData(
      response,
      [200, 202],
      "The refund request returned an invalid response.",
    );
  } catch (error) {
    throw dateScopedRefundError(
      error,
      "The refund request could not be completed.",
    );
  }
}

async function getOperation(operationId) {
  const normalizedOperationId = requiredIdentifier(
    operationId,
    "A refund operation",
    "refund_operation_id_required",
  );
  try {
    const response = await apiAdminClient.get(
      `/refund/operations/${encodeURIComponent(normalizedOperationId)}`,
      undefined,
      { config: { timeout: OPERATION_STATUS_TIMEOUT_MS } },
    );
    return normalizedResponseData(
      response,
      [200],
      "The refund status returned an invalid response.",
    );
  } catch (error) {
    throw dateScopedRefundError(
      error,
      "The refund status could not be loaded.",
    );
  }
}

function createOperationKey() {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    const uuid = cryptoApi.randomUUID();
    if (IDEMPOTENCY_KEY_PATTERN.test(uuid)) return uuid;
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return `date-refund:${Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  throw serviceError(
    "Secure operation-key generation is unavailable.",
    "secure_random_unavailable",
  );
}

export const dateScopedRefundService = {
  GUEST_FEE_DISPOSITIONS,
  quoteByDates,
  refundByDates,
  getOperation,
  createOperationKey,
};
